import {
  type Ancla,
  type Contenido,
  MAX_TRAMOS_POR_MARCA,
  textoDeBloque,
} from "@gestiondoc/shared";

/**
 * Fase 4 · de la selección del navegador sobre la hoja a las anclas de una marca: qué bloques toca,
 * con qué desplazamientos sobre su texto, y dónde poner el globo. Nada de React: solo DOM.
 */

/** Dónde va el globo, en píxeles desde la esquina del marco de la hoja. */
export type Lugar = { arriba: number; izquierda: number };

export const ANCHO_GLOBO = 320;

export type SeleccionLeida =
  | { que: "nada" }
  | { que: "problema"; texto: string; lugar: Lugar }
  | { que: "ancla"; anclas: Ancla[]; lugar: Lugar };

/** Lo que se puede marcar en la hoja: párrafos, títulos y el título de cada sección. */
const SELECTOR_DE_TRAMOS = "p[data-bloque], h2[data-bloque], h3[data-bloque], span[data-titulo]";

/**
 * Lo que hay seleccionado dentro de la hoja: una frase que se puede marcar (con dónde poner el
 * globo), o por qué no se puede. "Nada" si no hay selección o está fuera de la hoja.
 */
export function seleccionEnLaHoja(marco: HTMLElement, contenido: Contenido): SeleccionLeida {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return { que: "nada" };
  const r = sel.getRangeAt(0);
  const dentro = (n: Node) =>
    marco.contains(n) &&
    !(n.nodeType === Node.ELEMENT_NODE ? (n as Element) : n.parentElement)?.closest("[data-globo]");
  if (!dentro(r.startContainer) || !dentro(r.endContainer) || !r.toString().trim()) {
    return { que: "nada" };
  }
  // El globo va debajo del final de la selección (arriba, el teléfono pone su propio menú).
  const rects = r.getClientRects();
  const ultimo = rects[rects.length - 1] ?? r.getBoundingClientRect();
  const caja = marco.getBoundingClientRect();
  const lugar: Lugar = {
    arriba: ultimo.bottom - caja.top + 8,
    izquierda: Math.max(8, Math.min(ultimo.left - caja.left, marco.clientWidth - ANCHO_GLOBO - 8)),
  };
  const anclas = anclasDeRango(r, marco, contenido);
  if (anclas.length > MAX_TRAMOS_POR_MARCA) {
    return {
      que: "problema",
      lugar,
      texto: `Son demasiados párrafos para una sola marca (hasta ${MAX_TRAMOS_POR_MARCA}). Selecciona menos, o deja un comentario general.`,
    };
  }
  if (anclas.length > 0) return { que: "ancla", anclas, lugar };
  return {
    que: "problema",
    lugar,
    texto:
      "Aquí no hay texto que se pueda marcar (el encabezado, el control de actualizaciones, las listas de verificación). Usa un comentario general.",
  };
}

/**
 * Los tramos de un rango, uno por cada párrafo o título que toca, en el orden de la hoja. Una
 * selección que abarca varios párrafos da varios tramos (una sola marca). Los desplazamientos se
 * miden con el texto del DOM, que es el de `textoDe` (los números de marca y los paréntesis de
 * las referencias son CSS, no texto), y se comprueban contra el contenido antes de proponerlos.
 */
function anclasDeRango(r: Range, marco: HTMLElement, contenido: Contenido): Ancla[] {
  const anclas: Ancla[] = [];
  for (const el of marco.querySelectorAll(SELECTOR_DE_TRAMOS)) {
    if (!r.intersectsNode(el)) continue;
    // El pedazo del rango que cae dentro de este bloque.
    const parte = document.createRange();
    parte.selectNodeContents(el);
    if (r.compareBoundaryPoints(Range.START_TO_START, parte) > 0) {
      parte.setStart(r.startContainer, r.startOffset);
    }
    if (r.compareBoundaryPoints(Range.END_TO_END, parte) < 0) {
      parte.setEnd(r.endContainer, r.endOffset);
    }
    const antes = document.createRange();
    antes.selectNodeContents(el);
    antes.setEnd(parte.startContainer, parte.startOffset);
    let inicio = antes.toString().length;
    let citado = parte.toString();
    // Sin los espacios de las orillas: una doble pulsación en una palabra suele llevarse uno.
    const izquierda = citado.length - citado.trimStart().length;
    citado = citado.trim();
    inicio += izquierda;
    if (!citado) continue;
    const bloqueId = el.getAttribute("data-bloque") ?? el.getAttribute("data-titulo") ?? "";
    const texto = textoDeBloque(contenido, bloqueId);
    if (texto?.slice(inicio, inicio + citado.length) !== citado) continue;
    anclas.push({ bloqueId, inicio, fin: inicio + citado.length, citado });
  }
  return anclas;
}
